import { CreditCard, Gauge, Receipt, Info } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { useLoad } from '../hooks/useLoad';
import { useOrg } from '../auth/OrgContext';
import { PageHeader, Card, StatCard, Badge, EmptyState, cx } from '../components/ui';
import { UsageSection } from './UsagePage';

// Billing (PRD §8): read-only POC view — plan + metered usage + mock invoices. Live Stripe billing
// is Alpha; here usage is recorded and invoices are mock (is_mock), no charges.
interface Tenant {
  tier: string;
  status: string;
  plan?: string;
  platform_fee_cents: number;
  unit_price_cents: number;
  pricing?: { platform_fee_cents: number; unit_price_cents: number };
  recipient_count?: number;
  enterprise_eligible?: boolean;
}
const PLAN_LABELS: Record<string, string> = {
  single_tenant: 'Single-Tenant',
  multi_tenant: 'Multi-Tenant',
  enterprise: 'Enterprise',
};
interface Usage {
  period: string;
  total_units: number;
}
interface Invoice {
  id: string;
  period: string;
  unit_count: number;
  total_cents: number;
  status: string;
  is_mock: boolean;
}
const usd = (c: number) => `$${(c / 100).toFixed(2)}`;

export function BillingPage() {
  const { withOrg, selectedOrgId } = useOrg();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'usage' ? 'usage' : 'overview';
  const t = useLoad(() => api.get<Tenant>('/v1/tenant'), []);
  const u = useLoad(() => api.get<Usage>(withOrg('/v1/usage')), [selectedOrgId]);
  const inv = useLoad(() => api.get<{ data: Invoice[] }>('/v1/invoices'), []);
  const tenant = t.data;
  const usage = u.data;
  const invoices = inv.data?.data ?? [];
  // Resolved plan pricing (FR-1): GET /v1/tenant already resolves these; pricing{} is the explicit object.
  const planFee = tenant?.pricing?.platform_fee_cents ?? tenant?.platform_fee_cents ?? 0;
  const planUnit = tenant?.pricing?.unit_price_cents ?? tenant?.unit_price_cents ?? 0;
  const projected = tenant && usage ? planFee + usage.total_units * planUnit : 0;

  return (
    <div>
      <PageHeader
        title="Billing"
        subtitle="Plan, metered usage, and invoices."
        actions={<Badge tone="muted">POC · mock billing</Badge>}
      />

      <div className="mb-6 flex gap-1 border-b border-line">
        {[
          { key: 'overview', label: 'Overview' },
          { key: 'usage', label: 'Usage' },
        ].map((x) => (
          <button
            key={x.key}
            onClick={() => setParams(x.key === 'overview' ? {} : { tab: x.key }, { replace: true })}
            className={cx(
              '-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
              tab === x.key
                ? 'border-ink text-ink'
                : 'border-transparent text-muted hover:text-ink',
            )}
          >
            {x.label}
          </button>
        ))}
      </div>

      {tab === 'usage' ? (
        <UsageSection />
      ) : (
        <>
          {/* FR-2: Plan panel — named plan, monthly retention fee, per-send price, enterprise note. */}
          <Card className="mb-6 p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="eyebrow mb-1">Your plan</div>
                <div className="text-lg font-semibold text-ink">
                  {tenant ? (PLAN_LABELS[tenant.plan ?? ''] ?? tenant.plan ?? tenant.tier) : '—'}
                </div>
                <div className="mt-3 flex gap-8 text-sm">
                  <div>
                    <div className="text-muted">Monthly data retention</div>
                    <div className="tabular-nums text-ink">
                      {tenant ? `${usd(planFee)}/mo` : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted">Per send</div>
                    <div className="tabular-nums text-ink">
                      {tenant ? `${usd(planUnit)}/send` : '—'}
                    </div>
                  </div>
                </div>
                <div className="mt-1 text-xs text-muted">
                  Unlimited emails, users, and features on every plan.
                </div>
              </div>
              {tenant?.enterprise_eligible && (
                <Badge tone="outline">Enterprise pricing available</Badge>
              )}
            </div>
            <div className="mt-4 flex items-start gap-2 border-t border-line pt-3 text-sm text-muted">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Enterprise custom pricing available for tenants syncing more than 5,000 users.{' '}
                <a className="font-medium text-ink underline" href="mailto:sales@runpavlov.ai">
                  Contact us
                </a>
                .
              </span>
            </div>
          </Card>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              label="Plan"
              value={tenant ? (PLAN_LABELS[tenant.plan ?? ''] ?? tenant.plan ?? tenant.tier) : '—'}
              icon={CreditCard}
              hint={tenant ? `${usd(planFee)}/mo base · ${usd(planUnit)}/send` : undefined}
            />
            <StatCard
              label={`Units · ${usage?.period ?? 'this period'}`}
              value={String(usage?.total_units ?? 0)}
              icon={Gauge}
              hint="metered at send-accept"
            />
            <StatCard
              label="Projected this period"
              value={usd(projected)}
              icon={Receipt}
              hint="base + units × unit price"
            />
          </div>

          <div className="eyebrow mb-3 mt-8">Invoices</div>
          <Card className="overflow-hidden">
            {invoices.length === 0 ? (
              <EmptyState
                icon={Receipt}
                title="No invoices yet"
                hint="A mock invoice is generated per billing period."
              />
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
                  <tr>
                    <th className="px-5 py-3">Period</th>
                    <th className="px-5 py-3">Units</th>
                    <th className="px-5 py-3">Total</th>
                    <th className="px-5 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {invoices.map((i) => (
                    <tr key={i.id}>
                      <td className="px-5 py-3 text-ink">{i.period}</td>
                      <td className="px-5 py-3 tabular-nums text-muted">{i.unit_count}</td>
                      <td className="px-5 py-3 tabular-nums text-ink">{usd(i.total_cents)}</td>
                      <td className="px-5 py-3">
                        <Badge tone={i.is_mock ? 'muted' : 'outline'}>
                          {i.is_mock ? 'mock' : i.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <div className="mt-4 flex items-start gap-2 rounded-input border border-line bg-ink/[0.02] px-4 py-3 text-sm text-muted">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            Live metered billing via Stripe arrives in Alpha (PRD §8.3). During the POC, usage is
            recorded and invoices are mock — no charges are made.
          </div>
        </>
      )}
    </div>
  );
}
